import React from 'react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  FolderKanban, AlertTriangle, MapPin, Target, TrendingUp,
  CheckCircle2, ListTodo, MoreHorizontal, Loader2, Building2,
} from 'lucide-react';
import { ImageWithFallback } from './figma/ImageWithFallback';
import { useDashboardOverview } from '../hooks/api/dashboard';
import { useNavigation, useMe } from '../hooks/useMe';
import { SuperAdminDashboard } from './SuperAdminDashboard';

// Visual-only placeholder images, indexed deterministically by project id so
// the same project always gets the same picture across reloads.
const PROJECT_IMAGES = [
  'https://images.unsplash.com/photo-1546998256-3529a72762b0?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&w=1080',
  'https://images.unsplash.com/photo-1669003153363-6d7ba8e20c7e?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&w=1080',
  'https://images.unsplash.com/photo-1739706815629-06f3a2b6a1c4?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&w=1080',
  'https://images.unsplash.com/photo-1614366502473-e54666693b44?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&w=1080',
  'https://images.unsplash.com/photo-1672073311074-f60c4a5e7b92?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&w=1080',
  'https://images.unsplash.com/photo-1758465828635-cb04ee439095?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&w=1080',
];
function imageFor(id = '') {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return PROJECT_IMAGES[h % PROJECT_IMAGES.length];
}

const STATUS_COLORS = {
  Active:        'bg-green-500',
  'In Progress': 'bg-blue-500',
  Planning:      'bg-yellow-500',
  Completed:     'bg-gray-500',
  'On Hold':     'bg-orange-500',
};

export function Dashboard({ onProjectClick, onJumpToReports, onJumpToAnalytics, onJumpToAlerts }) {
  const me = useMe();
  // Both super-admins and client users use the same hierarchical drill-down.
  // Client users are pinned to their primary client (no "All Clients" crumb)
  // and start at the Industries level. Super-admins start at Clients.
  return (
    <SuperAdminDashboard
      initialClientId={me?.is_super_admin ? null : (me?.clients?.[0]?.id || null)}
      pinnedToClient={!me?.is_super_admin}
      onJumpToReports={onJumpToReports}
      onJumpToAnalytics={onJumpToAnalytics}
      onJumpToAlerts={onJumpToAlerts}
    />
  );
}

// Legacy flat dashboard kept (unused) for reference. Remove later.
function _LegacyClientDashboard({ onProjectClick }) {
  const { data: overview, isLoading: overviewLoading } = useDashboardOverview();
  const navigation = useNavigation();
  const me = useMe();

  const k = overview?.kpis;
  const fmt = (n) => (overviewLoading ? '…' : n != null ? n : '—');

  // Flatten the user's permitted Industry → Project tree once for the cards
  const allProjects = React.useMemo(() => {
    const out = [];
    (navigation?.industries || []).forEach((ind) => {
      (ind.projects || []).forEach((p) => {
        out.push({
          id: p.id,
          code: p.code,
          name: p.name,
          industry: ind.name,
          deliverableCount: (p.deliverables || []).length,
        });
      });
    });
    return out;
  }, [navigation]);

  // For per-project status/progress/location we'd need extra fields on the
  // navigation payload; the dashboard cards show count + industry + image,
  // and clicking drills down into the real Projects page.
  return (
    <div className="space-y-4">
      {/* KPI cards — live */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="border-border bg-card hover:shadow-lg transition-shadow">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <p className="text-sm font-medium text-muted-foreground">Active Inspections</p>
                <h3 className="text-2xl font-bold text-foreground">{fmt(k?.active_inspections)}</h3>
                <div className="flex items-center text-xs text-blue-500">
                  <ListTodo className="w-3 h-3 mr-1" /> In progress
                </div>
              </div>
              <div className="w-10 h-10 rounded-xl bg-blue-500 flex items-center justify-center">
                <ListTodo className="w-5 h-5 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border bg-card hover:shadow-lg transition-shadow">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <p className="text-sm font-medium text-muted-foreground">Completed (30 days)</p>
                <h3 className="text-2xl font-bold text-foreground">{fmt(k?.completed_inspections_30d)}</h3>
                <div className="flex items-center text-xs text-green-500">
                  <CheckCircle2 className="w-3 h-3 mr-1" /> Last 30 days
                </div>
              </div>
              <div className="w-10 h-10 rounded-xl bg-green-500 flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border bg-card hover:shadow-lg transition-shadow">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <p className="text-sm font-medium text-muted-foreground">Total Projects</p>
                <h3 className="text-2xl font-bold text-foreground">{fmt(k?.total_projects)}</h3>
                <div className="flex items-center text-xs text-green-500">
                  <TrendingUp className="w-3 h-3 mr-1" /> Across your tenants
                </div>
              </div>
              <div className="w-10 h-10 rounded-xl bg-purple-500 flex items-center justify-center">
                <FolderKanban className="w-5 h-5 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border bg-card hover:shadow-lg transition-shadow">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <p className="text-sm font-medium text-muted-foreground">Open Alerts</p>
                <h3 className="text-2xl font-bold text-foreground">{fmt(k?.open_alerts)}</h3>
                <div className="flex items-center text-xs text-orange-500">
                  <Target className="w-3 h-3 mr-1" /> Unread notifications
                </div>
              </div>
              <div className="w-10 h-10 rounded-xl bg-orange-500 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Clients section — real, from /auth/me */}
      {(me?.clients?.length ?? 0) > 0 && (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-1 h-6 bg-blue-600 rounded-full"></div>
            <h2 className="text-xl font-bold text-foreground">Clients</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {me.clients.map((c) => (
              <Card
                key={c.id}
                className="border-white/5 bg-white/5 backdrop-blur-md hover:bg-white/10 transition-all duration-300 group relative"
              >
                <CardContent className="p-4 flex items-center gap-4">
                  <div className="w-20 h-20 bg-white rounded-xl flex items-center justify-center p-2 shrink-0 shadow-sm border border-white/10 overflow-hidden">
                    <Building2 className="w-10 h-10 text-blue-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-lg text-white truncate">{c.name}</h4>
                    <p className="text-xs text-white/60 font-mono">{c.code}</p>
                    {c.is_client_admin && (
                      <Badge variant="outline" className="mt-1 text-[10px]">Client Admin</Badge>
                    )}
                  </div>
                  <Button
                    variant="ghost" size="icon"
                    className="absolute top-2 right-2 text-muted-foreground hover:text-white opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <MoreHorizontal className="w-4 h-4" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Projects grid — fully DB-backed via /auth/me/navigation */}
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <div className="w-1 h-6 bg-purple-600 rounded-full"></div>
          <h2 className="text-xl font-bold text-foreground">Projects</h2>
        </div>

        {!navigation && (
          <div className="flex items-center gap-2 py-8 justify-center text-sm text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading projects…
          </div>
        )}

        {navigation && allProjects.length === 0 && (
          <Card className="border-border bg-card">
            <CardContent className="py-10 text-center text-muted-foreground">
              <p className="mb-1">No projects assigned to your workspace yet.</p>
              <p className="text-xs">A super admin can enable projects for your client from the Clients page.</p>
            </CardContent>
          </Card>
        )}

        {allProjects.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {allProjects.map((project) => (
              <div
                key={project.id}
                className="cursor-pointer group"
                onClick={() => onProjectClick?.(project.id, project.name)}
              >
                <Card className="border-border bg-card hover:shadow-lg transition-all duration-300 hover:scale-105 overflow-hidden">
                  <CardContent className="p-0 relative h-64">
                    <ImageWithFallback
                      src={imageFor(project.id)}
                      alt={project.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
                    <div className="absolute bottom-0 left-0 right-0 p-4 space-y-2">
                      <Badge variant="outline" className="bg-black/40 text-white border-white/30 backdrop-blur-sm">
                        {project.code}
                      </Badge>
                      <h4 className="font-semibold text-white text-lg line-clamp-2">{project.name}</h4>
                      <div className="flex items-center text-sm text-white/90">
                        <MapPin className="w-4 h-4 mr-1" />
                        {project.industry}
                      </div>
                      <div className="text-xs text-white/80">
                        {project.deliverableCount} deliverable{project.deliverableCount === 1 ? '' : 's'}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Keep a default export for any legacy imports.
export default Dashboard;
